import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Menu, 
  X, 
  FileWarning, 
  Trash2, 
  Clock, 
  ScanLine, 
  Bell, 
  BarChart3, 
  Smartphone,
  Check,
  ArrowRight,
  Leaf
} from "lucide-react";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import PrivacyPage from "./PrivacyPage";

const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const consent = getCookieConsentValue("AllerFoodControlCookies");
    
    if (consent === "true") {
      window.dataLayer = window.dataLayer || [];
      
      // 1. Primero confirmamos el estado del consentimiento
      window.dataLayer.push({ 
        event: 'cookie_consent_status', // Cambiamos el nombre para diferenciar del clic
        cookie_status: 'accepted'
      });
      
      // 2. Enviamos la página vista con un retraso mínimo para que GA4 esté listo
      setTimeout(() => {
        window.dataLayer.push({
          event: 'page_view',
          page_path: location.pathname + location.search
        });
      }, 100); 
    }
  }, [location]);

  return null;
};


// Landing Page Component
const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleWaitlistSubmit = async (e) => {
  e.preventDefault();

  if (!email) return;

  try {
    const response = await fetch("https://formspree.io/f/xeepvdbp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email, name, restaurant }),
    });

    if (response.ok) {
        window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'waitlist_signup',
      restaurant: restaurant 
    });
      if (window.fbq) {
        window.fbq('track', 'Lead'); 
        }
      setFormSubmitted(true);
      setEmail("");
      setRestaurant("");
      setName("");
      setTimeout(() => setFormSubmitted(false), 3000);
    } else {
      console.error("Error enviando formulario");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

  const scrollToSection = (id) => {
  if (window.location.pathname !== '/') {
    window.location.href = `/#${id}`; // Esto fuerza la vuelta a la home
  } else {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

  return (
    <div className="min-h-screen bg-neutral-50 font-body">

      {/* Hero Section */}
      <section className="pt-28 lg:pt-36 pb-20 lg:pb-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-left animate-fade-in-up">
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-tight mb-6">
                Deja de tirar dinero a la basura.{" "}
                <span className="text-emerald-800">Controla tu inventario y mermas con IA.</span>
              </h1>
              <p className="text-slate-600 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
                La solución definitiva para que los restaurantes digitalicen su inventario, 
                rastreen fechas de caducidad y maximicen sus márgenes desde una tablet o móvil.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => scrollToSection('waitlist')}
                  className="bg-emerald-800 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg font-medium btn-press"
                  data-testid="hero-cta-primary"
                >
                  Ahorra un 15% en mermas hoy
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right - Dashboard Mockup */}
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="ambient-glow">
                <div className="feature-image-container">
                  <img 
                    src="../mockup1.png"
                    alt="Dashboard de Aller Food Control mostrando análisis de inventario"
                    className="feature-image w-full rounded-2xl dashboard-shadow animate-float"
                    data-testid="hero-dashboard-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="benefits" className="py-24 md:py-32 bg-white" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-left mb-16">
            <p className="text-sm uppercase tracking-widest text-emerald-800 font-semibold mb-4">
              El problema
            </p>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-slate-900 tracking-tight">
              El costo de la gestión tradicional
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div 
              className="problem-card bg-white border border-slate-200 rounded-2xl p-8 card-hover animate-fade-in-up"
              data-testid="problem-card-errors"
            >
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-6">
                <FileWarning className="w-6 h-6 text-emerald-800" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">
                Errores manuales
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Inventarios inexactos que llevan a compras innecesarias y pérdidas de stock crítico.
              </p>
            </div>

            {/* Card 2 */}
            <div 
              className="problem-card bg-white border border-slate-200 rounded-2xl p-8 card-hover animate-fade-in-up animation-delay-200"
              data-testid="problem-card-waste"
            >
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-6">
                <Trash2 className="w-6 h-6 text-emerald-800" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">
                Mermas por caducidad
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Pérdidas de miles de euros al mes en ingredientes caducados que podrían haberse evitado.
              </p>
            </div>

            {/* Card 3 */}
            <div 
              className="problem-card bg-white border border-slate-200 rounded-2xl p-8 card-hover animate-fade-in-up animation-delay-400"
              data-testid="problem-card-time"
            >
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-emerald-800" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">
                Papeleo interminable
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Horas perdidas del personal en tareas administrativas que deberían automatizarse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-neutral-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-left mb-16">
            <p className="text-sm uppercase tracking-widest text-emerald-800 font-semibold mb-4">
              La solución
            </p>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-slate-900 tracking-tight">
              Todo lo que necesitas, automatizado
            </h2>
          </div>

          {/* Feature Grid - Bento Style */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Feature 1 - Large */}
            <div 
              className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12 card-hover"
              data-testid="feature-ai-scanning"
            >
              <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center mb-6">
                <ScanLine className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-slate-900 mb-4">
                Escaneo de Facturas con IA
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Fotografía tus albaranes y facturas. Nuestra IA extrae automáticamente los productos, 
                cantidades y precios para actualizar tu inventario al instante.
              </p>
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <span>Tecnología foto-a-stock</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Feature 2 */}
            <div 
              className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12 card-hover"
              data-testid="feature-alerts"
            >
              <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center mb-6">
                <Bell className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-slate-900 mb-4">
                Alertas Inteligentes de Caducidad
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Recibe notificaciones automáticas cuando un producto esté próximo a caducar. 
                El personal de cocina puede actuar a tiempo y reducir mermas.
              </p>
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <span>Notificaciones personalizables</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Feature 3 */}
            <div 
              className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12 card-hover"
              data-testid="feature-dashboard"
            >
              <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-slate-900 mb-4">
                Dashboard de Rentabilidad
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Visualiza en tiempo real el costo de tus mermas, márgenes por producto y 
                tendencias de inventario con analíticas profesionales.
              </p>
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <span>Analíticas en tiempo real</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Feature 4 */}
            <div 
              className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12 card-hover relative overflow-hidden"
              data-testid="feature-mobile"
            >
              <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center mb-6">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-slate-900 mb-4">
                Diseño Mobile-First para Cocinas
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                App optimizada para tablets y móviles. Diseñada para el ritmo frenético 
                de la cocina profesional. Funciona incluso sin conexión.
              </p>
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <span>Tecnología Ionic</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Chef Image */}
          <div className="mt-16 grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="../chef1.jpg"
                alt="Chef profesional usando tablet en cocina moderna"
                className="w-full rounded-3xl shadow-lg"
                data-testid="feature-chef-image"
              />
            </div>
            <div className="order-1 md:order-2">
              <h3 className="font-heading font-semibold text-2xl lg:text-3xl text-slate-900 mb-4">
                Diseñado para el mundo real de la hostelería
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                Sabemos que en la cocina no hay tiempo que perder. Por eso Aller Food Control está diseñado 
                para ser rápido, intuitivo y resistente. Tu equipo lo dominará en minutos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-emerald-800 font-semibold mb-4">
              Precios
            </p>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-slate-900 tracking-tight">
              Planes que crecen contigo
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-center max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div 
              className="bg-white border border-slate-200 rounded-3xl p-8 card-hover"
              data-testid="pricing-starter"
            >
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                Starter
              </h3>
              <p className="text-slate-500 mb-6">Para pequeños locales</p>
              <div className="mb-6">
                <span className="font-heading font-bold text-4xl text-slate-900">Gratis</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>1 ubicación</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>50 productos</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>Alertas básicas</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>Soporte por email</span>
                </li>
              </ul>
              <Button 
                variant="outline"
                onClick={() => scrollToSection('waitlist')}
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full py-6"
                data-testid="pricing-starter-cta"
              >
                Empezar Gratis
              </Button>
            </div>

            {/* Pro Plan - Featured */}
            <div 
              className="bg-emerald-800 rounded-3xl p-8 md:p-10 pricing-pro shadow-xl shadow-emerald-900/20"
              data-testid="pricing-pro"
            >
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-heading font-semibold text-xl text-white">
                  Pro
                </h3>
                <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Popular
                </span>
              </div>
              <p className="text-emerald-100 mb-6">Gestión integral con IA</p>
              <div className="mb-6">
                <span className="font-heading font-bold text-4xl text-white">€49</span>
                <span className="text-emerald-100">/mes</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                  <span>Ubicaciones ilimitadas</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                  <span>Productos ilimitados</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                  <span>Escaneo IA de facturas</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                  <span>Dashboard de rentabilidad</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                  <span>Soporte prioritario</span>
                </li>
              </ul>
              <Button 
                onClick={() => {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'click_pricing_plan',
                        plan_name: 'Pro'
                    });
                    scrollToSection('waitlist')}}
                
                className="w-full bg-white text-emerald-800 hover:bg-emerald-50 rounded-full py-6 font-semibold"
                data-testid="pricing-pro-cta"
              >
                Ahorra un 15% en mermas hoy
              </Button>
            </div>

            {/* Enterprise Plan */}
            <div 
              className="bg-white border border-slate-200 rounded-3xl p-8 card-hover"
              data-testid="pricing-enterprise"
            >
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                Enterprise
              </h3>
              <p className="text-slate-500 mb-6">Para cadenas y franquicias</p>
              <div className="mb-6">
                <span className="font-heading font-bold text-4xl text-slate-900">Contactar</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>Todo de Pro</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>API personalizada</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>Integraciones ERP</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                  <span>Gerente de cuenta dedicado</span>
                </li>
              </ul>
              <Button 
                variant="outline"
                onClick={() => scrollToSection('waitlist')}
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full py-6"
                data-testid="pricing-enterprise-cta"
              >
                Contactar Ventas
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="py-24 md:py-32 bg-neutral-50" data-testid="waitlist-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 tracking-tight mb-6">
              Sé el primero en probar Aller Food Control
            </h2>
            <p className="text-slate-600 text-lg mb-10">
              Únete a la lista de espera y obtén acceso anticipado a nuestra plataforma. 
              Los primeros 20 usuarios recibirán 3 meses gratis del plan Pro.
            </p>

            <form 
              onSubmit={handleWaitlistSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto"
            >
            
              <Input 
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="waitlist-input flex-1 h-14 rounded-full px-6 border-slate-300 focus:border-emerald-800 text-lg"
                data-testid="waitlist-name-input"
                required
              />
              <Input 
                type="text"
                placeholder="Tu restaurante"
                value={restaurant}
                onChange={(e) => setRestaurant(e.target.value)}
                className="waitlist-input flex-1 h-14 rounded-full px-6 border-slate-300 focus:border-emerald-800 text-lg"
                data-testid="waitlist-restaurant-input"
                required
              />
              <Input 
                type="email"
                placeholder="Tu correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="waitlist-input flex-1 h-14 rounded-full px-6 border-slate-300 focus:border-emerald-800 text-lg"
                data-testid="waitlist-email-input"
                required
              />
              <div className="flex items-start mb-4">
                <input 
                    id="privacy" 
                    type="checkbox" 
                    className="mt-1 h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    required 
                />
                <label htmlFor="privacy" className="ml-2 text-sm text-gray-600">
                    He leído y acepto la <a href="/privacidad" className="underline hover:text-emerald-700">Política de Privacidad</a>.
                </label>
              </div>
              <Button 
                type="submit"
                className="bg-emerald-800 hover:bg-emerald-700 text-white rounded-full h-14 px-8 text-lg font-medium btn-press whitespace-nowrap"
                data-testid="waitlist-submit-button"
              >
                Únete a la Lista de Espera
              </Button>
            </form>

            {formSubmitted && (
              <div 
                className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 animate-fade-in-up"
                data-testid="waitlist-success-message"
              >
                ¡Gracias! Te hemos añadido a la lista de espera. Pronto recibirás noticias.
              </div>
            )}

            <p className="text-sm text-slate-500 mt-6">
              Sin spam. Solo actualizaciones importantes del producto.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 md:py-24 bg-slate-800" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2 text-white font-heading font-bold text-xl">
              <Leaf className="w-6 h-6" />
              Aller Food Control
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 Aller Software Factory. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
      const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
      const scrollToSection = (id) => {
  if (window.location.pathname !== '/') {
    window.location.href = `/#${id}`; // Esto fuerza la vuelta a la home
  } else {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

  return (
    <div className="App">
      <BrowserRouter>
      <RouteChangeTracker />
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <a 
              href="/" 
              className="flex items-center gap-2 font-heading font-bold text-xl text-emerald-800"
              data-testid="logo-link"
            >
              <Leaf className="w-6 h-6" />
              Aller Food Control
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('features')}
                className="nav-link text-slate-600 hover:text-emerald-800 transition-colors font-medium"
                data-testid="nav-features"
              >
                Características
              </button>
              <button 
                onClick={() => scrollToSection('benefits')}
                className="nav-link text-slate-600 hover:text-emerald-800 transition-colors font-medium"
                data-testid="nav-benefits"
              >
                Beneficios
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="nav-link text-slate-600 hover:text-emerald-800 transition-colors font-medium"
                data-testid="nav-pricing"
              >
                Precios
              </button>
              <Button 
                onClick={() => scrollToSection('waitlist')}
                className="bg-emerald-800 hover:bg-emerald-700 text-white rounded-full px-6 btn-press"
                data-testid="nav-cta-button"
              >
                Acceso Anticipado
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200/50 mobile-menu-enter" data-testid="mobile-menu">
              <div className="flex flex-col gap-4">
                <button 
                  onClick={() => scrollToSection('features')}
                  className="text-left text-slate-600 hover:text-emerald-800 font-medium py-2"
                  data-testid="mobile-nav-features"
                >
                  Características
                </button>
                <button 
                  onClick={() => scrollToSection('benefits')}
                  className="text-left text-slate-600 hover:text-emerald-800 font-medium py-2"
                  data-testid="mobile-nav-benefits"
                >
                  Beneficios
                </button>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="text-left text-slate-600 hover:text-emerald-800 font-medium py-2"
                  data-testid="mobile-nav-pricing"
                >
                  Precios
                </button>
                <Button 
                  onClick={() => scrollToSection('waitlist')}
                  className="bg-emerald-800 hover:bg-emerald-700 text-white rounded-full w-full"
                  data-testid="mobile-nav-cta"
                >
                  Acceso Anticipado
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />
        </Routes>
        <CookieConsent
          location="bottom"
          buttonText="Aceptar"
          declineButtonText="Rechazar"
          enableDeclineButton
          cookieName="AllerFoodControlCookies"
          style={{ background: "#1e293b", fontSize: "14px", fontFamily: "Inter, sans-serif" }}
          buttonStyle={{ background: "#065f46", color: "#fff", fontSize: "14px", borderRadius: "999px", padding: "10px 25px" }}
          declineButtonStyle={{ background: "transparent", color: "#94a3b8", fontSize: "14px" }}
          expires={150}
          onAccept={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                event: 'cookie_consent_accepted'
                });
            }}
        >
          Este sitio web utiliza cookies para mejorar la experiencia del usuario y analizar el tráfico. 
          Puedes leer más en nuestra <a href="/privacidad" className="underline text-emerald-400">política de privacidad</a>.
        </CookieConsent>
      </BrowserRouter>
    </div>
  );
}

export default App;
